import { useState } from 'react';
import { SlidersHorizontal, X, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface FilterState {
  priceRange: [number, number];
  brands: string[];
  sizes: string[];
  colors: string[];
  ratings: number[];
  discount: number;
  sortBy: string;
}

interface ProductFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  availableBrands: string[];
  availableSizes: string[];
  availableColors: string[];
  maxPrice: number;
  productCount: number;
}

const ratingOptions = [4, 3, 2, 1];
const discountOptions = [10, 20, 30, 40, 50];

export const ProductFilters = ({
  filters,
  onFiltersChange,
  availableBrands,
  availableSizes,
  availableColors,
  maxPrice,
  productCount
}: ProductFiltersProps) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    price: true,
    brand: true,
    size: true,
    color: false,
    rating: false,
    discount: false
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleArrayFilter = (key: 'brands' | 'sizes' | 'colors' | 'ratings', value: string | number) => {
    const current = filters[key] as (string | number)[];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, updated as FilterState[typeof key]);
  };

  const activeFilterCount = 
    filters.brands.length + 
    filters.sizes.length + 
    filters.colors.length + 
    filters.ratings.length + 
    (filters.discount > 0 ? 1 : 0) +
    (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      priceRange: [0, maxPrice],
      brands: [],
      sizes: [],
      colors: [],
      ratings: [],
      discount: 0,
      sortBy: 'featured'
    });
  };

  const FilterSection = ({ title, section, children }: { title: string; section: string; children: React.ReactNode }) => (
    <div className="border-b border-border pb-4 mb-4">
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between py-2 text-left font-medium"
      >
        {title}
        {expandedSections[section] ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      <AnimatePresence>
        {expandedSections[section] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const FilterContent = () => (
    <div className="space-y-2">
      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 pb-4 border-b">
          {filters.brands.map(brand => (
            <Badge key={brand} variant="secondary" className="gap-1">
              {brand}
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('brands', brand)} />
            </Badge>
          ))}
          {filters.sizes.map(size => (
            <Badge key={size} variant="secondary" className="gap-1">
              Size: {size}
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('sizes', size)} />
            </Badge>
          ))}
          {filters.colors.map(color => (
            <Badge key={color} variant="secondary" className="gap-1">
              {color}
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('colors', color)} />
            </Badge>
          ))}
          {filters.ratings.map(rating => (
            <Badge key={rating} variant="secondary" className="gap-1">
              {rating}★ & above
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('ratings', rating)} />
            </Badge>
          ))}
          {filters.discount > 0 && (
            <Badge variant="secondary" className="gap-1">
              {filters.discount}% off
              <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('discount', 0)} />
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-destructive hover:text-destructive">
            Clear All
          </Button>
        </div>
      )}

      {/* Price Range */}
      <FilterSection title="Price Range" section="price">
        <div className="px-2">
          <Slider
            value={filters.priceRange}
            min={0}
            max={maxPrice}
            step={100}
            onValueChange={(value) => updateFilter('priceRange', value as [number, number])}
            className="mb-4"
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>₹{filters.priceRange[0]}</span>
            <span>₹{filters.priceRange[1]}</span>
          </div>
        </div>
      </FilterSection>

      {/* Brand */}
      {availableBrands.length > 0 && (
        <FilterSection title="Brand" section="brand">
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableBrands.map(brand => (
              <label key={brand} className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg">
                <Checkbox
                  checked={filters.brands.includes(brand)}
                  onCheckedChange={() => toggleArrayFilter('brands', brand)}
                />
                <span className="text-sm">{brand}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Size */}
      {availableSizes.length > 0 && (
        <FilterSection title="Size" section="size">
          <div className="flex flex-wrap gap-2">
            {availableSizes.map(size => (
              <button
                key={size}
                onClick={() => toggleArrayFilter('sizes', size)}
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  filters.sizes.includes(size)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-primary/50"
                )}
              >
                {size}
              </button>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Color */}
      {availableColors.length > 0 && (
        <FilterSection title="Color" section="color">
          <div className="flex flex-wrap gap-2">
            {availableColors.map(color => (
              <button
                key={color}
                onClick={() => toggleArrayFilter('colors', color)}
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  filters.colors.includes(color)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-primary/50"
                )}
              >
                {color}
              </button>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Rating */}
      <FilterSection title="Customer Rating" section="rating">
        <div className="space-y-2">
          {ratingOptions.map(rating => (
            <label key={rating} className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg">
              <Checkbox
                checked={filters.ratings.includes(rating)}
                onCheckedChange={() => toggleArrayFilter('ratings', rating)}
              />
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    className={cn(
                      "h-4 w-4",
                      star <= rating ? "text-amber-500 fill-amber-500" : "text-muted"
                    )}
                  />
                ))}
                <span className="text-sm ml-1">& above</span>
              </div>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Discount */}
      <FilterSection title="Discount" section="discount">
        <div className="space-y-2">
          {discountOptions.map(discount => (
            <label key={discount} className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg">
              <Checkbox
                checked={filters.discount === discount}
                onCheckedChange={() => updateFilter('discount', filters.discount === discount ? 0 : discount)}
              />
              <span className="text-sm">{discount}% or more</span>
            </label>
          ))}
        </div>
      </FilterSection>
    </div>
  );

  return (
    <>
      {/* Desktop Filters */}
      <div className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 bg-card rounded-2xl border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </h3>
            <span className="text-sm text-muted-foreground">{productCount} products</span>
          </div>
          <FilterContent />
        </div>
      </div>

      {/* Mobile Filters */}
      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">{activeFilterCount}</Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5" />
                Filters
              </SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <FilterContent />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};